import axios from 'axios'
import type { AxiosError, AxiosResponse } from 'axios'
import { renewToken, forceRelogin } from '@/services/auth/oidc'

interface ErrorResponse {
  detail?: string | Record<string, unknown>
}

interface RequestConfig {
  jwt?: string
  _retried?: boolean
  [key: string]: unknown
}

interface StreamConfig {
  onData?: (data: any) => void
  onError?: (error: any) => void
  onComplete?: () => void
  dataPrefix?: string
}

export default {
  getToken() {
    return localStorage.getItem('access_token')
  },

  get(url: string, params = {}, config = {}) {
    return this.submitAuth('get', url, params, config)
  },

  post(url: string, data = {}, config = {}) {
    return this.submitAuth('post', url, data, config)
  },

  put(url: string, data = {}, config = {}) {
    return this.submitAuth('put', url, data, config)
  },

  patch(url: string, data = {}, config = {}) {
    return this.submitAuth('patch', url, data, config)
  },

  delete(url: string, data = {}, config = {}) {
    return this.submitAuth('delete', url, data, config)
  },

  _streamFetch(url: string, data: object) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getToken()}`,
      },
      body: JSON.stringify(data),
    })
  },

  async stream(url: string, data = {}, streamConfig: StreamConfig = {}) {
    const { onData, onError, onComplete, dataPrefix = 'data: ' } = streamConfig

    try {
      let response = await this._streamFetch(url, data)

      if (response.status === 401 && (await renewToken())) {
        response = await this._streamFetch(url, data)
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (reader) {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            if (onComplete) onComplete()
            break
          }

          const chunk = decoder.decode(value)
          const lines = chunk.split('\n').filter((line) => line.trim())

          for (const line of lines) {
            try {
              if (line.startsWith(dataPrefix)) {
                const jsonStr = line.substring(dataPrefix.length)
                const data = JSON.parse(jsonStr)
                if (onData) onData(data)
              }
            } catch (e) {
              console.warn('Error parsing stream data:', e)
              if (onError) onError(e)
            }
          }
        }
      }
    } catch (error) {
      if (onError) onError(error)
      throw error instanceof Error ? error.message : 'Stream error'
    }
  },

  async submitAuth(
    method: string,
    url: string,
    data = {},
    config: RequestConfig = {},
  ): Promise<AxiosResponse> {
    try {
      return await this._submitAuth(method, url, data, config)
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response?.status === 401 && !config._retried && !config.jwt) {
        if (await renewToken()) {
          return this.submitAuth(method, url, data, { ...config, _retried: true })
        }
        await forceRelogin()
      }
      throw this._handleError(axiosError)
    }
  },

  _submitAuth(method: string, url: string, data = {}, configRequest: any = {}) {
    method = method.toLowerCase()
    configRequest = {
      method: method,
      url: url,
      params: method === 'get' ? data : undefined,
      data: method !== 'get' ? data : undefined,
      headers: {
        Accept: 'application/json',
        Authorization: configRequest.jwt ? `JWT ${configRequest.jwt}` : `Bearer ${this.getToken()}`,
        'Content-Type':
          data instanceof window.FormData ? 'multipart/form-data' : 'application/json',
      },
      ...configRequest,
    }
    return axios(configRequest)
  },

  _handleError(error: AxiosError) {
    let message = 'Server error'
    const errorData = error.response?.data as ErrorResponse
    const detail = errorData?.detail
    if (typeof detail === 'string') {
      message = detail
    } else if (detail && typeof detail === 'object') {
      // Structured error bodies (e.g. Project validation) carry a
      // human-readable `message` field alongside machine-readable codes/ids.
      message = typeof detail.message === 'string' ? detail.message : JSON.stringify(detail)
    }
    return message
  },
}
